import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, BookOpenIcon, ChevronDownIcon, HeartIcon, HomeIcon, LaptopIcon, RecycleIcon, ShirtIcon } from 'lucide-react';
import { modules, VolumeIcon } from '../data/content';
import { Eyebrow, Highlight, SectionLead, SectionTitle } from './ui/Section';
const collapseTransition = {
  duration: 0.22,
  ease: [0.23, 1, 0.32, 1] as const
};
const icons: Record<VolumeIcon, typeof ShirtIcon> = {
  shirt: ShirtIcon,
  home: HomeIcon,
  laptop: LaptopIcon,
  heart: HeartIcon,
  recycle: RecycleIcon
};
export function Modules() {
  const [open, setOpen] = useState(0);
  return <section className="bg-slate-50 px-4 py-12 md:py-16" data-id="element-203" data-mp="1i5nso8bc9tm5">
      <div className="mx-auto max-w-4xl" data-id="element-204" data-mp="1i78khq69qygb">
        <Eyebrow data-id="element-205" data-mp="2olpy4e1qeq8c">
          <span className="inline-flex items-center gap-2" data-id="element-206" data-mp="2om9azgciyofi">
            <BookOpenIcon className="h-4 w-4" data-id="element-207" data-mp="2oo3ucq85e8hh" /> What the complete system includes
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-208" data-mp="1v2uzdu4zk0j">
          <Highlight data-id="element-209" data-mp="1vcp9dm7sdc1">5 volumes.</Highlight> From crowded spaces to clear decisions.
        </SectionTitle>
        <SectionLead data-id="element-210" data-mp="3tylyznpdg9l0">
          You are not receiving random organization tips. You are getting practical decision
          systems for the most common physical-clutter categories inside the home.
        </SectionLead>

        <div className="mt-8 space-y-3" data-id="element-211" data-mp="3jzwhhw1e0r5a">
          {modules.map((mod, index) => {
          const Icon = icons[mod.icon];
          const isOpen = open === index;
          return <div key={mod.name} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-150 ease-out ${isOpen ? 'border-blue-950 shadow-lg shadow-slate-200' : 'border-slate-200'}`} data-id="element-212" data-mp="3aa336n9v1xxc" data-mpi={index}>
                <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-6" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? -1 : index)} data-id="element-213" data-mp="2wa23hb5c0wjg">
                  <span className="flex items-center gap-4" data-id="element-214" data-mp="2mnhnz9b75w81">
                    <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${mod.color}`} data-id="element-215" data-mp="2mp2jb15c5j6y">
                      <Icon className="h-6 w-6 text-amber-300" aria-hidden="true" data-id="element-216" data-mp="2cos5bebh87qq" />
                    </span>
                    <span data-id="element-217" data-mp="30emglrg5do8v">
                      <small className="block text-xs font-extrabold uppercase tracking-wide text-yellow-700" data-id="element-218" data-mp="30ffpaxyo2ntn">
                        {mod.volume}
                      </small>
                      <b className="block text-sm font-black uppercase text-blue-950 md:text-lg" data-id="element-219" data-mp="2rlj7twgg5zkk">
                        {mod.name}
                      </b>
                    </span>
                  </span>
                  <ChevronDownIcon className={`h-5 w-5 shrink-0 text-blue-950 transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} data-id="element-220" data-mp="3jcyrpiqmd873" />
                </button>
                <AnimatePresence initial={false} data-id="element-221" data-mp="1cnopdst6cnta">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={collapseTransition} data-id="element-222" data-mp="17pn6p8znuqic">
                      <div className="border-t border-slate-200 px-5 py-5 md:px-6" data-id="element-223" data-mp="trotw80zn24y">
                        <p className="text-sm font-bold text-slate-700 md:text-base" data-id="element-224" data-mp="os2js7ggi20d">{mod.intro}</p>

                        {mod.transforms && <div className="mt-4" data-id="element-225" data-mp="13q4io4cjlzba">
                            <p className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-226" data-mp="yp72fxb3ffcf">
                              This volume helps turn:
                            </p>
                            <ul className="mt-2 space-y-2" data-id="element-227" data-mp="2a55hchsycvlk">
                              {mod.transforms.map(([from, to]) => <li key={from} className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-600" data-id="element-228" data-mp="jotgozgoxqgf">
                                  <span className="font-black text-blue-950" data-id="element-229" data-mp="sqknk7myw3bv">{from}</span>
                                  <span data-id="element-230" data-mp="xoulgq02ti16">into</span>
                                  <ArrowRightIcon className="h-4 w-4 text-yellow-700" aria-hidden="true" data-id="element-231" data-mp="12n5uiyijroic" />
                                  <span className="rounded bg-amber-100 px-1.5 font-black text-blue-950" data-id="element-232" data-mp="17kxixijzgshs">
                                    {to}
                                  </span>
                                </li>)}
                            </ul>
                            <p className="mt-4 text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-233" data-mp="1ldnjcs09dzyf">
                              Includes:
                            </p>
                          </div>}

                        <ul className="mt-3 flex flex-wrap gap-2" data-id="element-234" data-mp="20gyieofmb7mv">
                          {mod.content.map(line => <li key={line} className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700" data-id="element-235" data-mp="1vj5c7vebedml">
                              {line}
                            </li>)}
                        </ul>
                        <div className="mt-4 space-y-1" data-id="element-236" data-mp="2e7a8khsn6wbd">
                          {mod.closing.map(line => <p key={line} className="text-sm font-black text-blue-950 md:text-base" data-id="element-237" data-mp="29fs9jza6j5t1">
                              {line}
                            </p>)}
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>
    </section>;
}